import {Component, effect, inject, input, OnInit, output} from '@angular/core'
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms'
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card'
import {MatOption} from '@angular/material/core'
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker'
import {MatFormField, MatHint, MatLabel, MatSuffix} from '@angular/material/form-field'
import {MatInput} from '@angular/material/input'
import {MatSelect} from '@angular/material/select'
import {FormatNumberDirective, getTakeUntilDestroyed} from '@sparbanken-syd/sparbanken-syd-theme'
import {switchMap} from 'rxjs/operators'
import {BuildingTypes, KalpBuilding} from '../../../model/kalp-building'
import {ControlService} from '../../../services/control.service'
import {KalpService, ObjectTypes, Scenarios} from '../../../services/kalp.service'

export const AMORTIZATION_CONSTANTS = {
  RECOMMENDED_AMORTIZATION_TEXT: {
    SINGLE: (percentage: number, monthlyCost: string) =>
      `Din belåningsgrad understiger 50% och det finns inget formellt amorteringskrav. ` +
      `Vi rekommenderar dock att du amorterar ${percentage.toLocaleString('sv-SE')}% (${monthlyCost} ` +
      `kr/månad) på dina bolån då det är sunt att amortera. ` +
      `Om du amorterar minskar din skuld till banken och därmed även dina räntekostnader.`,
    PLURAL: (percentage: number, monthlyCost: string) =>
      `Er belåningsgrad understiger 50% och det finns inget formellt amorteringskrav. ` +
      `Vi rekommenderar dock att ni amorterar ${percentage.toLocaleString('sv-SE')}% (${monthlyCost} ` +
      `kr/månad) på era bolån då det är sunt att amortera. ` +
      `Om ni amorterar minskar er skuld till banken och därmed även era räntekostnader.`
  },
  DEFAULT_RECOMMENDED_PERCENTAGE: 0.005
} as const

export const LOAN_DEGREE_LIMIT = 0.5

@Component({
  selector: 'spb-property',
  templateUrl: './property.component.html',
  styleUrls: ['./property.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatSuffix,
    FormatNumberDirective,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatCard,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatHint
  ]
})
export class PropertyComponent implements OnInit {
  public controlService = inject(ControlService)
  /**
   * The parent tells us which index we are
   */
  public readonly building = input<KalpBuilding>(new KalpBuilding())

  /**
   * The parent tells us which scenario we are running.
   */
  public readonly scenario = input<Scenarios>(Scenarios.Purchase)

  /**
   *
   */
  public readonly seekedAmount = input(0)

  /**
   * Any changes go here.
   */
  public readonly changes = output<KalpBuilding>()

  /**
   * Simple export to the template.
   */
  public scenarioTypes = [Scenarios.Purchase, Scenarios.Increase, Scenarios.Conditions, Scenarios.Transfer]

  /**
   * Simple export to the template.
   */
  public objectTypes = [ObjectTypes.VILLA, ObjectTypes.BOSTADSRATT, ObjectTypes.FRITIDSHUS]

  /**
   * This is our form.
   */
  public form = new FormGroup<any>({
    id: new FormControl(),
    typeOfObject: new FormControl(undefined, Validators.required),
    objectValue: new FormControl('', {
      validators: [Validators.required],
      updateOn: 'blur'
    }),
    mortgageRequirementValue: new FormControl('', {updateOn: 'blur'}),
    mortgageRequirementDebt: new FormControl('', {updateOn: 'blur'}),
    mortgageRequirementDate: new FormControl(null),
    fee: new FormControl(undefined, Validators.required),
    runCost: new FormControl(undefined, Validators.required),
    recommendedAmortization: new FormControl(AMORTIZATION_CONSTANTS.DEFAULT_RECOMMENDED_PERCENTAGE)
  })

  public loanDegree = 0
  public kalpPlusTwoPercent = 0

  private readonly takeUntilDestroyed = getTakeUntilDestroyed()
  private readonly kalpService = inject(KalpService)

  get revaluationDateText(): string | null {
    const dateVal = this.form.controls.mortgageRequirementDate.value
    if (!dateVal) {
      return null
    }

    const d = new Date(dateVal)
    // Add 5 years
    d.setFullYear(d.getFullYear() + 5)

    // Format YYYY-MM-DD
    const dateString = d.toISOString().split('T')[0]
    return `Bostad kan omvärderas tidigast ${dateString}`
  }

  get objectType(): ObjectTypes {
    return this.form.controls.typeOfObject.value
  }

  get showFee(): boolean {
    const feeObjects = [ObjectTypes.BOSTADSRATT, ObjectTypes.HYRESRATT]
    return feeObjects.includes(this.objectType)
  }

  get showValue(): boolean {
    const feeObjects = [ObjectTypes.BOSTADSRATT, ObjectTypes.VILLA, ObjectTypes.FRITIDSHUS]
    const propOb = [BuildingTypes.purchase, BuildingTypes.keep]
    return feeObjects.includes(this.objectType) && propOb.includes(this.building().type)
  }

  get rentOrFee(): string {
    return this.objectType === ObjectTypes.HYRESRATT ? 'Hyra' : 'Avgift'
  }

  constructor() {
    effect(() => {
      const b = this.building()
      this.form.patchValue({
        ...b,
        // Manually set getter/setter properties that patchValue can't understand
        runCost: b.runCost,
        mortgageRequirementValue: b.mortgageRequirementValue,
        mortgageRequirementDebt: b.mortgageRequirementDebt,
        mortgageRequirementDate: b.mortgageRequirementDate ?? null
      }, {emitEvent: false})
    })
  }

  public ngOnInit(): void {
    this.kalpService.kalpResult$.pipe(
      this.takeUntilDestroyed(),
      switchMap((kalpResult) => {
        this.kalpPlusTwoPercent = kalpResult[2].kalp
        return this.kalpService.getActiveBuilding()
      })
    ).subscribe((b) => {
      this.loanDegree = b.degree

      const isLowDegree = this.loanDegree <= LOAN_DEGREE_LIMIT && this.building().primary

      const lowDegreeValue = this.kalpPlusTwoPercent < 0 ? 0 : AMORTIZATION_CONSTANTS.DEFAULT_RECOMMENDED_PERCENTAGE
      const targetValue = isLowDegree ? lowDegreeValue : 0

      // Guard against infinite loop as setValue triggers valueChanges
      if (this.form.controls.recommendedAmortization.value !== targetValue) {
        this.form.controls.recommendedAmortization.setValue(targetValue)
      }
    })

    const types = [BuildingTypes.leave, BuildingTypes.other]
    if (types.includes(this.building().type)) {
      this.objectTypes.push(ObjectTypes.HYRESRATT)
      this.form.controls.objectValue.clearValidators()
      this.form.updateValueAndValidity()
    }

    this.form.valueChanges
      .pipe(this.takeUntilDestroyed())
      .subscribe((val: KalpBuilding) => {
        this.building().valid = this.form.valid
        this.changes.emit(Object.assign(this.building(), val))

        /**
           * send the seeked loan amount to kalp service if "bostadens värde is updated"
           */
        this.kalpService.loanAmount$.next(this.seekedAmount())
        this.kalpService.recalculate()
      })

    this.form.controls.objectValue.valueChanges
      .pipe(this.takeUntilDestroyed())
      .subscribe((val: number) => {
        if (!this.form.controls.mortgageRequirementValue.touched) {
          this.form.controls.mortgageRequirementValue.setValue(val)
        }
      })
  }

  /**
   * This is run every time the type of Object changes.
   */
  public setTypeOfObject(): void {
    // We take the type of object from the control and update our "building"
    // with that value
    // Set the run cost to default
    this.building().runCost = -1
    this.form.controls.runCost.setValue(this.building().runCost)
    if (this.showFee) {
      this.form.controls.fee.setValue(this.building().fee)
    } else {
      this.form.controls.fee.setValue(0)
    }


    if (!this.showValue) {
      this.form.controls.objectValue.setValue('')
    }

    this.form.controls.runCost.updateValueAndValidity()
  }
}
